import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  XMarkIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchAuditorsCompaniesApi, fetchSecurityAnalystsCompaniesApi } from "../../services/axios/AuthApis/AdministrationApis";
import { companyPerPage } from "../../constants/AppEnums";

const CompanySelectionModal = ({ isOpen, onSelect, previousCompany, required = false }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(isOpen);
  const userDetail = useSelector((state) => state.user);
  const userRole = userDetail?.user?.role;
  const userId = userDetail?.user?.id;
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const companiesPerPage = companyPerPage;
  const [totalPages, setTotalPages] = useState(0);

  const handleNext = () => {
    if (currentPage < totalPages - 1) { setCompanies([]); setCurrentPage((p) => p + 1); }
  };
  const handlePrev = () => {
    if (currentPage > 0) { setCompanies([]); setCurrentPage((p) => p - 1); }
  };
  const handleClose = () => setModalOpen(false);

  const getUserCompanies = async () => {
    setLoading(true);
    try {
      let res;
      const page = currentPage > 0 ? currentPage : 1;
      if (userRole === "security_analyst") {
        res = await fetchSecurityAnalystsCompaniesApi(page, companiesPerPage, userId, "choose");
      } else if (userRole === "auditor") {
        res = await fetchAuditorsCompaniesApi(userId, page, companiesPerPage, "choose");
      }
      if (res) {
        const sorted = [...res.data].sort((a, b) =>
          (a.company_name || a.name || "").localeCompare(b.company_name || b.name || "")
        );
        setCompanies(sorted);
        setTotalPages(Math.ceil(res.totalCount / companiesPerPage));
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getUserCompanies(); }, [currentPage]);

  return ReactDOM.createPortal(
    <>
      {/* Scoped hover styles using CSS variable */}
      <style>{`
        .csm-card:hover { border-color: var(--app-primary-color, #26256b) !important; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
        .csm-card:hover .csm-icon-wrap { border-color: var(--app-primary-color, #26256b); opacity: 0.7; }
        .csm-card:hover .csm-name { color: var(--app-primary-color, #26256b); }
        .csm-page-btn:not(:disabled):hover { border-color: var(--app-primary-color, #26256b); color: var(--app-primary-color, #26256b); }
      `}</style>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={required ? undefined : handleClose}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh] overflow-hidden"
              initial={{ opacity: 0, y: -24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -24, scale: 0.97 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="flex items-center gap-4 px-6 py-5 rounded-t-2xl"
                style={{ background: "var(--app-primary-color, #26256b)" }}
              >
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheckIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-white leading-tight">Select a Company</h2>
                  <p className="text-xs text-white/70 mt-0.5">
                    {required ? "You must select a company to continue" : "Choose the company you want to work with"}
                  </p>
                </div>
                {!required && (
                  <button
                    onClick={handleClose}
                    className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="px-6 py-5 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div
                      className="w-8 h-8 rounded-full border-2 border-gray-200 animate-spin"
                      style={{ borderTopColor: "var(--app-primary-color, #26256b)" }}
                    />
                    <p className="text-sm text-gray-500">Loading companies...</p>
                  </div>
                ) : companies.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <BuildingOffice2Icon className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">No companies found</p>
                    <p className="text-xs text-gray-400">There are no companies assigned to your account.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {companies.map((company) => {
                      const isSelected = previousCompany?.id === company.id;
                      return (
                        <button
                          key={company.id}
                          onClick={() => onSelect(company)}
                          className="csm-card group relative flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-200 text-left bg-white"
                          style={isSelected ? {
                            borderColor: "var(--app-primary-color, #26256b)",
                            boxShadow: "0 0 0 1px var(--app-primary-color, #26256b)",
                            backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 6%, white)",
                          } : { borderColor: "#E5E7EB" }}
                        >
                          {isSelected && (
                            <span className="absolute top-2 right-2">
                              <CheckCircleIcon className="w-4 h-4" style={{ color: "var(--app-primary-color, #26256b)" }} />
                            </span>
                          )}

                          <div
                            className="csm-icon-wrap w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 overflow-hidden transition-colors"
                            style={isSelected ? {
                              borderColor: "var(--app-primary-color, #26256b)",
                              backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 12%, white)",
                            } : { borderColor: "#E5E7EB", backgroundColor: "#F9FAFB" }}
                          >
                            {company.logo ? (
                              <img src={company.logo} alt={company.company_name || company.name} className="w-full h-full object-contain" />
                            ) : (
                              <BuildingOffice2Icon
                                className="w-5 h-5"
                                style={{ color: isSelected ? "var(--app-primary-color, #26256b)" : "#9CA3AF" }}
                              />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p
                              className="csm-name text-sm font-semibold truncate transition-colors"
                              style={isSelected ? { color: "var(--app-primary-color, #26256b)" } : { color: "#1F2937" }}
                            >
                              {company.company_name || company.name}
                            </p>
                            {company.location && (
                              <p className="flex items-center gap-1 text-xs text-gray-400 mt-0.5 truncate">
                                <MapPinIcon className="w-3 h-3 flex-shrink-0" />
                                {company.location}
                              </p>
                            )}
                            {isSelected && (
                              <p className="text-xs font-medium mt-0.5" style={{ color: "var(--app-primary-color, #26256b)" }}>
                                Currently active
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer — Pagination */}
              {totalPages > 1 && !loading && (
                <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 rounded-b-2xl flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    Page <span className="font-medium text-gray-600">{currentPage + 1}</span> of{" "}
                    <span className="font-medium text-gray-600">{totalPages}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 mr-3">
                      {Array.from({ length: totalPages }).map((_, index) => (
                        <button
                          key={index}
                          onClick={() => { setCompanies([]); setCurrentPage(index); }}
                          className="rounded-full transition-all duration-200"
                          style={currentPage === index
                            ? { width: 20, height: 8, backgroundColor: "var(--app-primary-color, #26256b)" }
                            : { width: 8, height: 8, backgroundColor: "#D1D5DB" }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={handlePrev}
                      disabled={currentPage === 0}
                      className="csm-page-btn w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 transition-all disabled:text-gray-300 disabled:cursor-not-allowed text-gray-600"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={currentPage >= totalPages - 1}
                      className="csm-page-btn w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 transition-all disabled:text-gray-300 disabled:cursor-not-allowed text-gray-600"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};

export default CompanySelectionModal;
